import { store } from '../app/store';
import { showToast, hideToast, ToastSeverity } from '../app/store/toastSlice';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface NotifyOptions {
  severity?: ToastSeverity;
  action?: ToastAction;
  // Override auto-hide (ms). Defaults: 6s for plain toasts, never for actionable ones.
  autoHideMs?: number | null;
}

// Action handlers can't live in Redux (must stay serializable), so we keep them
// here keyed by id and reference the id from the slice.
const actionRegistry = new Map<string, () => void>();
let actionCounter = 0;

/**
 * Show a user-facing toast. Optionally include an action button (e.g. Retry,
 * Reload, Contact support, or any custom callback) so you can tell the user
 * what to do next.
 *
 *   notify('Saved!', { severity: 'success' });
 *   notify('Couldn’t save. Try again?', { severity: 'error', action: { label: 'Retry', onClick: save } });
 */
export function notify(message: string, options: NotifyOptions = {}): void {
  const { severity = 'info', action } = options;

  let actionId: string | undefined;
  if (action) {
    actionCounter += 1;
    actionId = `toast-${actionCounter}`;
    actionRegistry.set(actionId, action.onClick);
  }

  store.dispatch(
    showToast({
      message,
      severity,
      actionLabel: action?.label,
      actionId,
      // Actionable toasts stay until dismissed so the user doesn't miss them.
      autoHideMs: options.autoHideMs ?? (action ? null : 6000),
    }),
  );
}

/** Invoked by the toast component when the action button is clicked. */
export function runToastAction(actionId: string): void {
  const handler = actionRegistry.get(actionId);
  actionRegistry.delete(actionId);
  store.dispatch(hideToast());
  handler?.();
}

export function dismissToast(): void {
  store.dispatch(hideToast());
}

/** Test-only: clears the in-memory action registry. */
export function clearToastActionRegistry(): void {
  actionRegistry.clear();
  actionCounter = 0;
}
